import React, { useEffect, useState } from "react";
import axios from "../../auth/auth";
import {
    FaPlus,
    FaEdit,
    FaEye,
    FaTrash,
    FaSave,
    FaBox,
    FaDollarSign
} from "react-icons/fa";

export default function ManageItemsTab() {
    // const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [mode, setMode] = useState("view"); // view | add | edit
    const [items, setItems] = useState([]);
    const [newItems, setNewItems] = useState([]);
    const [editedItems, setEditedItems] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // ================= FETCH =================
    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await axios.get("/quotations/master/capital-items");
            setItems(res.data || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    // ================= ADD =================
    const addRow = () => {
        setNewItems(prev => [
            ...prev,
            {
                item_name: "",
                item_code: "",
                sku: "",
                default_unit_price: ""
            }
        ]);
    };

    const handleNewChange = (index, field, value) => {
        const updated = [...newItems];
        updated[index][field] = value;
        setNewItems(updated);
    };

    const removeNewRow = (index) => {
        setNewItems(prev => prev.filter((_, i) => i !== index));
    };

    const validateNewItems = () => {
        for (let i = 0; i < newItems.length; i++) {
            const item = newItems[i];

            if (!item.item_name?.trim()) {
                return `Row ${i + 1}: Item name required`;
            }

            if (!item.default_unit_price || Number(item.default_unit_price) < 0) {
                return `Row ${i + 1}: Invalid price`;
            }
        }
        return null;
    };

    const saveNewItems = async () => {
        if (newItems.length === 0) {
            setMessage("Add at least one row before saving.");
            return;
        }

        const error = validateNewItems();
        if (error) {
            setMessage(error);
            return;
        }

        try {
            await axios.post("/quotations/master/capital-items/bulk", {
                items: newItems.map(i => ({
                    ...i,
                    default_unit_price: Number(i.default_unit_price)
                }))
            });

            setMessage("Items added successfully");
            setNewItems([]);
            setMode("view");
            fetchItems();
        } catch (err) {
            setMessage(err?.response?.data?.detail || "Add failed");
            alert(err?.response?.data?.detail)
        }
    };

    // ================= EDIT =================
    const handleEditChange = (id, field, value) => {
        setEditedItems(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [field]: value
            }
        }));
    };

    const saveEditedItems = async () => {
        try {
            await Promise.all(
                Object.keys(editedItems).map(id =>
                    axios.patch(
                        `quotations/master/capital-items/${id}`,
                        editedItems[id]
                    )
                )
            );

            setMessage("Items updated successfully");
            setEditedItems({});
            setMode("view");
            fetchItems();
        } catch {
            setMessage("Update failed");
        }
    };

    // ================= DELETE =================
    const deleteItem = async (id) => {
        const confirmDelete = window.confirm("Are you sure you want to delete?");
        if (!confirmDelete) return;

        try {
            await axios.delete(`/quotations/master/capital-items/${id}`);
            setMessage("Item deleted");
            fetchItems();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || err.response?.data?.message || "Delete failed";
            alert(errorMessage);
            setMessage(errorMessage);
        }
    };

    const getEditValue = (id, field, defaultValue) => {
        return editedItems[id]?.[field] ?? defaultValue ?? "";
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow">

            {/* HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
                <div>
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                        <FaBox className="text-blue-600" />
                        Manage Capital Items
                    </h2>
                </div>

                {/* MODE BUTTONS */}
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => {
                            setMode("view");
                            setEditedItems({});
                            setNewItems([]);
                        }}
                        className={`px-3 py-2 rounded flex items-center gap-2 text-sm font-medium shadow-sm ${
                            mode === "view"
                                ? "bg-blue-500 text-white shadow"
                                : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                        <FaEye /> View
                    </button>

                    <button
                        onClick={() => {
                            setMode("add");
                            setEditedItems({});
                            setNewItems([]);
                        }}
                        className={`px-3 py-2 rounded flex items-center gap-2 text-sm font-medium shadow-sm ${
                            mode === "add"
                                ? "bg-blue-500 text-white shadow"
                                : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                        <FaPlus /> Add
                    </button>

                </div>
            </div>

            {/* MESSAGE */}
            {message && (
                <div className="mb-3 text-sm text-green-600">{message}</div>
            )}

            {/* LOADING */}
            {loading && <div className="py-4">Loading items...</div>}

            {/* ================= VIEW ================= */}
            {mode === "view" && !loading && (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th
                                    scope="col"
                                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                                >
                                    Product
                                </th>
                                <th
                                    scope="col"
                                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                                >
                                    SKU
                                </th>
                                <th
                                    scope="col"
                                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                                >
                                    Price
                                </th>
                                <th
                                    scope="col"
                                    className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {items.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500">
                                        No items found. Switch to <span className="font-semibold">Add</span> to create new entries.
                                    </td>
                                </tr>
                            ) : (
                                items.map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                                    <FaBox />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {item.item_name}
                                                    </div>
                                                    <div className="text-xs text-gray-500">{item.item_code}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                            {item.sku || "—"}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 flex items-center gap-1">
                                            <FaDollarSign className="text-gray-400" />
                                            <span>{item.default_unit_price}</span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => {
                                                    setMode("edit");
                                                    setEditedItems({
                                                        [item.id]: {
                                                            item_name: item.item_name,
                                                            sku: item.sku,
                                                            default_unit_price: item.default_unit_price
                                                        }
                                                    });
                                                }}
                                                className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-600 shadow-sm hover:bg-gray-50"
                                                title="Edit item"
                                            >
                                                <FaEdit />
                                            </button>

                                            <button
                                                onClick={() => deleteItem(item.id)}
                                                className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-red-600 shadow-sm hover:bg-red-50"
                                                title="Delete item"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ================= ADD ================= */}
            {mode === "add" && (
                <>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                        <button
                            onClick={addRow}
                            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                        >
                            <FaPlus /> Add Row
                        </button>
                    </div>

                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        Product Name
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        Code
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        SKU
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        Price
                                    </th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {newItems.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">
                                        No rows to add. Click "Add Row" to create a new item.
                                    </td>
                                </tr>
                            ) : (
                                newItems.map((item, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <input
                                                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                                                placeholder="Product name"
                                                value={item.item_name}
                                                onChange={e => handleNewChange(i, "item_name", e.target.value)}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                                                placeholder="Code"
                                                value={item.item_code}
                                                onChange={e => handleNewChange(i, "item_code", e.target.value)}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                                                placeholder="SKU"
                                                value={item.sku}
                                                onChange={e => handleNewChange(i, "sku", e.target.value)}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                type="number"
                                                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                                                placeholder="Price"
                                                value={item.default_unit_price}
                                                onChange={e => handleNewChange(i, "default_unit_price", e.target.value)}
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => removeNewRow(i)}
                                                className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-red-600 shadow-sm hover:bg-red-50"
                                                title="Remove row"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <button
                    onClick={saveNewItems}
                    className="mt-3 inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
                >
                    <FaSave /> Save
                </button>
            </>
        )}

            {/* ================= EDIT ================= */}
            {mode === "edit" && (
                <>
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        Product
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        SKU
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        Price
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {items.map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <input
                                                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                                                value={getEditValue(item.id, "item_name", item.item_name)}
                                                onChange={e =>
                                                    handleEditChange(item.id, "item_name", e.target.value)
                                                }
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                                                value={getEditValue(item.id, "sku", item.sku)}
                                                onChange={e => handleEditChange(item.id, "sku", e.target.value)}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                type="number"
                                                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                                                value={getEditValue(
                                                    item.id,
                                                    "default_unit_price",
                                                    item.default_unit_price
                                                )}
                                                onChange={e =>
                                                    handleEditChange(
                                                        item.id,
                                                        "default_unit_price",
                                                        Number(e.target.value)
                                                    )
                                                }
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <button
                            onClick={saveEditedItems}
                            className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
                        >
                            <FaSave /> Save Changes
                        </button>
                        <button
                            onClick={() => {
                                setMode("view");
                                setEditedItems({});
                            }}
                            className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}